import { useContext } from "react";
import { ReactNode } from "react";
import { isNil } from "lodash";
import { buildLink } from "common/routing";
import { formatFeet } from "common/utils/strings";
import { FIRMWithWarnings } from "common-client/utils/firmInfoWarnings";
import { getBaseFloodElevationConfiguration } from "common-client/utils/firms";
import { DisplayConfig } from "../../../../../../generated/graphql";
import { useIsScreenSize } from "../../../../../../hooks/useWindowSize";
import { track } from "../../../../../../utils/tracking";
import { AuthContext } from "../../../../../Authorization/AuthContext";
import { ApproximateBfeTool } from "../../../../../Maps/ApproximateBfeTool";
import { canUseApproximateBfeTool } from "../../../../../Maps/ApproximateBfeTool/utils";
import { LayerContext } from "../../../../../Maps/layers";
import { FIRMInfoContext } from "../../FIRMInfoContext";
import { RenderMode } from "../../types";
import { FIRMTableProps, FIRMTableTooltip } from "..";
import { RequestInfoLink } from "../__styles__/FIRMTable";
import { FIRMInfoRow } from "./FIRMInfoRow";

type BFERowProps = Pick<FIRMTableProps, "property" | "onFIRMEdit"> & {
  canUpdateFIRMInfo: boolean;
  hasRequestHelpOption?: boolean;
  label: string;
  isPublic: boolean;
  tooltip?: ReactNode;
  size: RenderMode;
  displayConfig?: DisplayConfig;
};

export const BFERow = ({
  property,
  onFIRMEdit,
  hasRequestHelpOption = false,
  isPublic: bfeAttributeIsPublic,
  tooltip,
  label,
  size,
  displayConfig,
}: BFERowProps) => {
  const { isPublic, authorized } = useContext(AuthContext);
  const { activeFirmIds, firms } = useContext(FIRMInfoContext);
  const { approximateBfeToolDispatch, visibleFIRM } = useContext(LayerContext);
  const activeFirms = firms.filter(firm => activeFirmIds.includes(firm.id));
  const isDesktop = useIsScreenSize("desktop");

  const { skipIfBlank } = getBaseFloodElevationConfiguration({
    firms: activeFirms,
    authorized,
    isPublic,
    hasRequestHelpOption,
  });

  const valueForFIRM = (firm: FIRMWithWarnings) => {
    const mapLayerFirm = visibleFIRM();

    const openApproximateBfeTool =
      canUseApproximateBfeTool({
        displayConfig,
        firm,
        isDesktop,
      }) &&
      property?.id &&
      mapLayerFirm &&
      !firm.stringStaticBFE;

    const onClick = openApproximateBfeTool
      ? () => {
          approximateBfeToolDispatch?.({
            type: "setMode",
            data: {
              mode: "on",
              render: () => {
                return (
                  <ApproximateBfeTool
                    openEditFIRM={() => onFIRMEdit({ firm, mode: "elevation" })}
                    propertyId={property.id}
                    mapLayerFirm={mapLayerFirm}
                  />
                );
              },
            },
          });
        }
      : () => onFIRMEdit({ firm, mode: "elevation" });

    const { value, canEdit, canRequestHelp } =
      getBaseFloodElevationConfiguration({
        selectedFirmId: firm.id,
        firms: [firm],
        authorized,
        isPublic,
        hasRequestHelpOption,
      });

    if (isNil(value) && canRequestHelp) {
      return (
        <RequestInfoLink
          to={buildLink(
            "getHelp",
            {},
            { propertyId: property?.id, reasonCodes: "requestBFE" },
          )}
          onClick={() => track("Request BFE")}
        >
          Request info
        </RequestInfoLink>
      );
    }

    const formattedValue = isNil(value) ? null : formatFeet(value);

    if (canEdit) {
      return <a onClick={onClick}>{formattedValue ?? "Add"}</a>;
    }

    return formattedValue ?? null;
  };

  const activeBFE = activeFirms[0]?.bfeSource;
  const finalTooltip = activeBFE ? (
    <FIRMTableTooltip tooltipMarkdown={activeBFE} isPublicOnly={false} />
  ) : (
    tooltip
  );

  return (
    <FIRMInfoRow
      property="stringStaticBFE"
      valueForFIRM={valueForFIRM}
      label={label}
      skipIfBlank={skipIfBlank}
      tooltip={finalTooltip}
      isPublic={bfeAttributeIsPublic}
      size={size}
    />
  );
};
